
import { motion } from 'framer-motion';
import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      return;
    }

    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d51pf2j8t6vgj5dk5d7g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Hero Section */}
        <section className="relative h-[50vh] w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/29b1132f3959e27994182d1cdb14b3df.jpg"
              alt="Contact Background"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
          </div>

          <div className="relative h-full flex items-center justify-center px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-center max-w-4xl"
            >
              <h1 className="font-playfair text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
                GET IN TOUCH
              </h1>
              <p className="font-lora text-xl text-white/90 leading-relaxed">
                I'd love to hear from you. Whether you have questions, feedback, or just want to connect
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-24 px-8 lg:px-16 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Left Column - Info */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="font-playfair text-4xl font-bold text-charcoal mb-6">
                  Let's Connect
                </h2>
                <p className="font-lato text-lg text-warm-gray leading-relaxed mb-8">
                  Whether you're interested in book signings, speaking engagements, book club discussions, or simply want to share your thoughts about my work, I welcome your message.
                </p>

                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-burgundy/10 rounded-lg flex-shrink-0">
                      <i className="ri-book-open-line text-xl text-burgundy"></i>
                    </div>
                    <div>
                      <h3 className="font-playfair text-xl font-bold text-charcoal mb-2">Book Clubs</h3>
                      <p className="font-lato text-warm-gray">I love connecting with book clubs and am available for virtual or in-person discussions.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-burgundy/10 rounded-lg flex-shrink-0">
                      <i className="ri-mic-line text-xl text-burgundy"></i>
                    </div>
                    <div>
                      <h3 className="font-playfair text-xl font-bold text-charcoal mb-2">Speaking Events</h3>
                      <p className="font-lato text-warm-gray">Available for literary festivals, conferences, and educational institutions.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-burgundy/10 rounded-lg flex-shrink-0">
                      <i className="ri-quill-pen-line text-xl text-burgundy"></i>
                    </div>
                    <div>
                      <h3 className="font-playfair text-xl font-bold text-charcoal mb-2">Signed Copies</h3>
                      <p className="font-lato text-warm-gray">Personalized signed books available upon request for special occasions.</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Right Column - Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
                  <div>
                    <label htmlFor="name" className="block font-lato text-sm text-charcoal mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-warm-gray/30 rounded-lg focus:outline-none focus:border-burgundy transition-colors text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block font-lato text-sm text-charcoal mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-warm-gray/30 rounded-lg focus:outline-none focus:border-burgundy transition-colors text-sm"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block font-lato text-sm text-charcoal mb-2">
                      Subject
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-warm-gray/30 rounded-lg focus:outline-none focus:border-burgundy transition-colors text-sm cursor-pointer"
                    >
                      <option value="">Select a subject</option>
                      <option value="Book Club Discussion">Book Club Discussion</option>
                      <option value="Speaking Engagement">Speaking Engagement</option>
                      <option value="Signed Copy Request">Signed Copy Request</option>
                      <option value="Media Inquiry">Media Inquiry</option>
                      <option value="General Question">General Question</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block font-lato text-sm text-charcoal mb-2">
                      Message * <span className="text-warm-gray/60">(Max 500 characters)</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-warm-gray/30 rounded-lg focus:outline-none focus:border-burgundy transition-colors resize-none text-sm"
                      placeholder="Share your thoughts, questions, or requests..."
                    ></textarea>
                    <p className="text-xs text-warm-gray/60 mt-1 text-right">
                      {formData.message.length}/500 characters
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-burgundy text-cream font-lato text-sm tracking-wider rounded-lg hover:bg-burgundy/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800 font-lato">
                        Thank you for your message! I'll get back to you soon.
                      </p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800 font-lato">
                        Something went wrong. Please try again later.
                      </p>
                    </div>
                  )}
                </form>
              </motion.div>
            </div>
          </div>
        </section>
      </motion.main>

      <Footer />
    </div>
  );
};

export default ContactPage;
